import { Badge, Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { FieldStatusDto } from "../../../../../../autogen/bff-api";
import type { PublicEventStep } from "../../wizardSteps";

export type EditTarget =
  | { kind: "wizard-step"; step: PublicEventStep }
  | { kind: "external"; href: string; label?: string }
  | { kind: "auto-saved" };

export interface SummarySectionProps {
  title: string;
  editTarget: EditTarget;
  fieldStatuses: FieldStatusDto[];
  variant?: "default" | "editable";
  onWizardStepNavigate?: (step: PublicEventStep) => void;
  children: ReactNode;
}

const computePill = (statuses: FieldStatusDto[]) => {
  if (statuses.length === 0) return null;
  const missing = statuses.filter((s) => !s.satisfied).length;
  return missing === 0 ? { kind: "complete" as const } : { kind: "missing" as const, count: missing };
};

export const SummarySection = ({
  title,
  editTarget,
  fieldStatuses,
  variant = "default",
  onWizardStepNavigate,
  children,
}: SummarySectionProps) => {
  const { t } = useTranslation();
  const pill = computePill(fieldStatuses);
  const editable = variant === "editable";

  return (
    <Box
      as="section"
      aria-label={title}
      borderWidth="1px"
      borderRadius="lg"
      borderColor={editable ? "smPrimarySoft" : "smBorder"}
      bg={editable ? "smPrimarySofter" : "smBackground"}
      p="5"
      mb="4"
    >
      <Flex align="center" justify="space-between" mb="3" gap="2" wrap="wrap">
        <Flex align="center" gap="2" minW="0">
          <Heading as="h3" size="sm">
            {title}
          </Heading>
          {pill?.kind === "complete" && (
            <Badge bg="smSuccessSoft" color="smSuccess" fontSize="2xs">
              {t("Complete")}
            </Badge>
          )}
          {pill?.kind === "missing" && (
            <Badge bg="smErrorSoft" color="smError" fontSize="2xs">
              {t("{{count}} missing", { count: pill.count })}
            </Badge>
          )}
        </Flex>
        {editTarget.kind === "wizard-step" && (
          <Button
            variant="link"
            color="smPrimary"
            size="sm"
            onClick={() => onWizardStepNavigate?.(editTarget.step)}
          >
            {t("Edit")}
          </Button>
        )}
        {editTarget.kind === "external" && (
          <Text
            as="a"
            href={editTarget.href}
            color="smPrimary"
            fontSize="sm"
            fontWeight="medium"
            _hover={{ textDecoration: "underline" }}
          >
            {editTarget.label ?? t("Edit")}
          </Text>
        )}
        {editTarget.kind === "auto-saved" && (
          <Text fontSize="xs" color="smMuted" fontStyle="italic">
            {t("Saves automatically")}
          </Text>
        )}
      </Flex>
      {children}
    </Box>
  );
};
